import { Controller } from "@hotwired/stimulus"

// Customer portal search palette (CustomerPortal::SearchPaletteComponent,
// CONEK-9458).
//
// The dialog's results are a turbo-frame this controller points at the
// portal's /search/quick: the query (once it has `minLength` characters) and
// the active filter go in its query string, and the server renders the rows.
//
//   * Filters ("pills"): one at a time. The active one shows as a token in the
//     input; clicking it again, its ✕, or Backspace in an empty input removes
//     it. With a filter on, an empty query still asks the server, which lists
//     that part of the portal with a row to its page first — highlighted, so
//     pill + Enter opens the page.
//   * Keyboard (APG combobox): focus stays in the input; ↑/↓ move a
//     highlighted option (aria-selected + aria-activedescendant), Enter opens
//     it, or the results page when none is highlighted. ⌘K / Ctrl+K opens the
//     palette from anywhere on the page; Escape closes it (native <dialog>).
//   * Recent searches: a row opened from the palette is kept in this browser
//     (localStorage, per login: `recentKey`) and offered while the palette is
//     empty. The
//     storage can be missing (private mode, blocked site data): every access
//     is guarded, and the palette works without it.
export default class extends Controller {
  static targets = [
    "dialog",
    "input",
    "token",
    "tokenLabel",
    "tokenRemove",
    "scope",
    "recent",
    "recentList",
    "frame",
    "metaKey",
  ]
  static values = {
    searchUrl: String,
    minLength: { type: Number, default: 3 },
    recentKey: String,
  }

  static STORAGE_KEY = "customer-portal-search-recent"
  static MAX_RECENT = 6
  static DEBOUNCE_MS = 200

  connect() {
    this.activeFilter = null
    this.selectedIndex = -1
    this.optionSeq = 0
    // ⌘ as rendered; Ctrl everywhere but Apple keyboards.
    if (this.hasMetaKeyTarget && !/Mac|iPhone|iPad/.test(navigator.platform)) {
      this.metaKeyTarget.textContent = "Ctrl"
    }
  }

  disconnect() {
    clearTimeout(this.debounce)
  }

  open() {
    if (this.dialogTarget.open) return
    this.dialogTarget.showModal()
    this.inputTarget.focus()
    this.refresh()
  }

  close() {
    this.dialogTarget.close()
  }

  // The dialog closed (✕, Escape, backdrop, or a row opened): start clean.
  onClose() {
    clearTimeout(this.debounce)
    this.inputTarget.value = ""
    this.setScope(null)
    this.clearFrame()
  }

  backdropClose(event) {
    if (event.target !== this.dialogTarget) return
    const box = this.dialogTarget.getBoundingClientRect()
    const inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom
    if (!inside) this.close()
  }

  globalKeydown(event) {
    if (event.key?.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey))
      return
    event.preventDefault()
    if (this.dialogTarget.open) this.close()
    else this.open()
  }

  onInput() {
    clearTimeout(this.debounce)
    this.debounce = setTimeout(
      () => this.refresh(),
      this.constructor.DEBOUNCE_MS,
    )
  }

  toggleScope({ params: { key, label } }) {
    this.setScope(this.activeFilter?.key === key ? null : { key, label })
    this.inputTarget.focus()
    this.refresh()
  }

  clearScope() {
    this.setScope(null)
    this.inputTarget.focus()
    this.refresh()
  }

  keydown(event) {
    if (event.isComposing) return
    const options = this.options()
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveTo(
          options,
          this.selectedIndex + 1 >= options.length ? 0 : this.selectedIndex + 1,
        )
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveTo(
          options,
          this.selectedIndex <= 0 ? options.length - 1 : this.selectedIndex - 1,
        )
        break
      case "Enter": {
        if (event.target !== this.inputTarget) return
        event.preventDefault()
        const option = options[this.selectedIndex]
        if (option) this.openOption(option)
        else if (this.query().length >= this.minLengthValue)
          window.location.href = this.url({ search: this.query() })
        break
      }
      case "Backspace":
        if (
          event.target === this.inputTarget &&
          this.inputTarget.value === "" &&
          this.activeFilter
        ) {
          event.preventDefault()
          this.clearScope()
        }
        break
    }
  }

  // The frame swapped in new rows: a filter's listing starts on its first row
  // (its page), a plain search on none (Enter opens the results page).
  onFrameLoad() {
    this.selectedIndex = -1
    const options = this.options()
    if (this.activeFilter && options.length > 0) this.moveTo(options, 0)
    else this.highlight(options)
  }

  hover(event) {
    const option = event.target.closest('[role="option"]')
    if (!option) return
    const options = this.options()
    const index = options.indexOf(option)
    if (index >= 0 && index !== this.selectedIndex) this.moveTo(options, index)
  }

  // A row clicked in the results: keep it before the link navigates.
  remember(event) {
    const option = event.target.closest("[data-recent-search-name]")
    if (option) this.saveRecent(option)
  }

  // ── private ──────────────────────────────────────────────────────────

  refresh() {
    const query = this.query()
    const searching = query.length >= this.minLengthValue
    this.recentTarget.classList.toggle(
      "hidden",
      Boolean(this.activeFilter) || query.length > 0 || !this.renderRecent(),
    )
    if (!this.activeFilter && !searching) {
      this.clearFrame()
      return
    }
    this.frameTarget.src = this.url({ q: searching ? query : "" })
  }

  url(params) {
    const url = new URL(this.searchUrlValue, window.location.origin)
    Object.entries(params).forEach(([key, value]) =>
      url.searchParams.set(key, value),
    )
    if (this.activeFilter) url.searchParams.set("scope", this.activeFilter.key)
    return url.toString()
  }

  query() {
    return this.inputTarget.value.trim()
  }

  setScope(scope) {
    this.activeFilter = scope
    this.scopeTargets.forEach((button) => {
      const active =
        scope?.key ===
        button.getAttribute("data-customer-portal--search-palette-key-param")
      button.dataset.active = String(active)
      button.setAttribute("aria-pressed", String(active))
    })
    this.tokenTarget.classList.toggle("hidden", !scope)
    this.tokenTarget.classList.toggle("flex", Boolean(scope))
    this.tokenLabelTarget.textContent = scope?.label ?? ""
    const label = scope
      ? this.tokenRemoveTarget.dataset.labelTemplate.replace(
          "%{filter}",
          scope.label,
        )
      : ""
    this.tokenRemoveTarget.setAttribute("aria-label", label)
  }

  clearFrame() {
    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = ""
    this.selectedIndex = -1
    this.highlight([])
  }

  options() {
    const recent = this.recentTarget.classList.contains("hidden")
      ? []
      : [...this.recentListTarget.querySelectorAll('[role="option"]')]
    return [...recent, ...this.frameTarget.querySelectorAll('[role="option"]')]
  }

  moveTo(options, index) {
    if (options.length === 0) return
    this.selectedIndex = index
    this.highlight(options)
  }

  highlight(options) {
    let activeId = null
    options.forEach((option, i) => {
      const active = i === this.selectedIndex
      if (!option.id)
        option.id = `customer-portal-search-option-${this.optionSeq++}`
      option.setAttribute("aria-selected", String(active))
      if (active) {
        activeId = option.id
        option.scrollIntoView({ block: "nearest" })
      }
    })
    if (activeId)
      this.inputTarget.setAttribute("aria-activedescendant", activeId)
    else this.inputTarget.removeAttribute("aria-activedescendant")
  }

  openOption(option) {
    if (option.dataset.recentSearchName) this.saveRecent(option)
    if (option.href) window.location.href = option.href
  }

  // Draws the recent searches; false when there are none.
  renderRecent() {
    const recent = this.readRecent()
    this.recentListTarget.replaceChildren(
      ...recent.map(({ name, href }) => {
        const link = document.createElement("a")
        link.href = href
        link.setAttribute("role", "option")
        link.tabIndex = -1
        link.dataset.recentSearchName = name
        link.dataset.recentSearchHref = href
        link.className =
          "bg-foreground/5 text-foreground-medium aria-selected:border-foreground/45 aria-selected:bg-foreground/10 aria-selected:text-foreground rounded-full border border-transparent px-2.5 py-1 text-xs outline-none"
        link.textContent = name
        return link
      }),
    )
    return recent.length > 0
  }

  saveRecent(option) {
    const name = option.dataset.recentSearchName
    const href = option.dataset.recentSearchHref
    if (!name || !href) return
    const recent = this.readRecent().filter((entry) => entry.href !== href)
    recent.unshift({ name, href })
    this.writeRecent(recent.slice(0, this.constructor.MAX_RECENT))
  }

  storageKey() {
    return `${this.constructor.STORAGE_KEY}:${this.recentKeyValue}`
  }

  readRecent() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey()) || "[]")
      return Array.isArray(stored)
        ? stored.filter(
            (entry) =>
              typeof entry?.name === "string" &&
              typeof entry?.href === "string" &&
              entry.href.startsWith("/"),
          )
        : []
    } catch {
      return []
    }
  }

  writeRecent(recent) {
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(recent))
    } catch {
      // Storage unavailable: the palette simply remembers nothing.
    }
  }
}
